import React, { useState } from "react";
import Axios from "axios";
import {
  FiArrowRight,
  FiBookOpen,
  FiCheckCircle,
  FiMapPin,
  FiPhone,
  FiUser,
} from "react-icons/fi";

const trimTrailingSlash = (value = "") => value.replace(/\/$/, "");
const publicUrl = trimTrailingSlash(process.env.PUBLIC_URL || "");
const contactFormEndpoint =
  (process.env.REACT_APP_CONTACT_FORM_ENDPOINT || "").trim() ||
  `${publicUrl}/send-contact-email.php`;

const initialForm = {
  name: "",
  email: "",
  mobile: "",
  currentClass: "",
  applyingFor: "",
  city: "",
};

const grades = [
  "Pre-Nursery",
  "Nursery",
  "Prep",
  "Class I",
  "Class II",
  "Class III",
  "Class IV",
  "Class V",
  "Class VI",
  "Class VII",
  "Class VIII",
  "Class IX",
  "Class X",
  "Class XI",
  "Class XII",
];

function FieldIcon({ children }) {
  return <span className="ad26-field-icon">{children}</span>;
}

function AdmissionEnquiry() {
  const [form, setForm] = useState(initialForm);
  const [interests, setInterests] = useState(["Boarding facilities"]);
  const [status, setStatus] = useState({ type: "idle", message: "" });

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const toggleInterest = (interest) => {
    setInterests((current) =>
      current.includes(interest)
        ? current.filter((item) => item !== interest)
        : [...current, interest]
    );
  };

  const submit = async (event) => {
    event.preventDefault();
    setStatus({ type: "loading", message: "Sending your enquiry…" });

    try {
      const response = await Axios.post(
        contactFormEndpoint,
        {
          form_type: "admission",
          username: form.name,
          email: form.email,
          mobile: form.mobile,
          city: form.city,
          grade: form.applyingFor,
          message: `Current class: ${form.currentClass}. Interested in: ${
            interests.join(", ") || "General admission information"
          }.`,
          school: "bhimtal",
        },
        { headers: { "Content-Type": "application/json" } }
      );

      const responseBody = response?.data ?? {};
      const successful =
        response.status >= 200 &&
        response.status < 300 &&
        (responseBody.msg === "Success" ||
          responseBody.message === "Your response sent successfully." ||
          responseBody.message === "Email Sent");

      if (!successful) {
        throw new Error(responseBody.message || "Unable to send your enquiry right now.");
      }

      setForm(initialForm);
      setInterests(["Boarding facilities"]);
      setStatus({
        type: "success",
        message: "Thank you. Our admissions team will contact you soon.",
      });
    } catch (error) {
      setStatus({
        type: "error",
        message:
          error?.response?.data?.message ||
          error?.message ||
          "Unable to send your enquiry right now. Please call our admissions team.",
      });
    }
  };

  const interestOptions = [
    "Boarding facilities",
    "Admission process",
    "Campus visit",
    "Academics",
    "Fees",
    "Safety & care",
  ];

  return (
    <aside className="ad26-enquiry" id="enquire">
      <div className="ad26-enquiry-heading">
        <span>Admissions 2027–2028</span>
        <h2>Get Boarding Information</h2>
        <p>Tell us what you need and our team will guide you.</p>
      </div>

      <form onSubmit={submit}>
        <label className="ad26-field">
          <span className="ad26-visually-hidden">Parent or guardian name</span>
          <FieldIcon><FiUser aria-hidden="true" /></FieldIcon>
          <input
            type="text"
            name="name"
            value={form.name}
            onChange={updateField}
            placeholder="Parent / guardian name*"
            autoComplete="name"
            required
          />
        </label>

        <label className="ad26-field">
          <span className="ad26-visually-hidden">Mobile or WhatsApp number</span>
          <FieldIcon><FiPhone aria-hidden="true" /></FieldIcon>
          <input
            type="tel"
            name="mobile"
            value={form.mobile}
            onChange={updateField}
            placeholder="Mobile / WhatsApp number*"
            autoComplete="tel"
            pattern="[0-9+() -]{7,18}"
            required
          />
        </label>

        <label className="ad26-field">
          <span className="ad26-visually-hidden">Email address</span>
          <FieldIcon><FiUser aria-hidden="true" /></FieldIcon>
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={updateField}
            placeholder="Email address*"
            autoComplete="email"
            required
          />
        </label>

        <label className="ad26-field">
          <span className="ad26-visually-hidden">Child's current class</span>
          <FieldIcon><FiBookOpen aria-hidden="true" /></FieldIcon>
          <input
            type="text"
            name="currentClass"
            value={form.currentClass}
            onChange={updateField}
            placeholder="Child’s current class*"
            required
          />
        </label>

        <label className="ad26-field">
          <span className="ad26-visually-hidden">Class applying for</span>
          <FieldIcon><FiBookOpen aria-hidden="true" /></FieldIcon>
          <select
            name="applyingFor"
            value={form.applyingFor}
            onChange={updateField}
            required
          >
            <option value="" disabled>Class applying for*</option>
            {grades.map((grade) => <option key={grade}>{grade}</option>)}
          </select>
        </label>

        <label className="ad26-field">
          <span className="ad26-visually-hidden">Current city</span>
          <FieldIcon><FiMapPin aria-hidden="true" /></FieldIcon>
          <input
            type="text"
            name="city"
            value={form.city}
            onChange={updateField}
            placeholder="Current city*"
            autoComplete="address-level2"
            required
          />
        </label>

        <fieldset className="ad26-interests">
          <legend>What would you like to know?</legend>
          <div>
            {interestOptions.map((interest) => (
              <label key={interest}>
                <input
                  type="checkbox"
                  checked={interests.includes(interest)}
                  onChange={() => toggleInterest(interest)}
                />
                <span>{interest}</span>
              </label>
            ))}
          </div>
        </fieldset>

        <button className="ad26-submit" type="submit" disabled={status.type === "loading"}>
          {status.type === "loading" ? "Sending…" : "Submit enquiry"}
          <FiArrowRight aria-hidden="true" />
        </button>

        {status.message && (
          <p className={`ad26-form-status ad26-form-${status.type}`} role="status">
            {status.type === "success" && <FiCheckCircle aria-hidden="true" />}
            {status.message}
          </p>
        )}
        <small className="ad26-privacy-note">
          We use your details only to respond to this admission enquiry.
        </small>
      </form>
    </aside>
  );
}

export default AdmissionEnquiry;
